/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMiiaFwS3 } from '@/features/miiaFw/composables/useMiiaFwS3'
import MiiaFwConfig from '@/configs/config'
import { useS3Utils } from './useS3Utils'
import type { Directory } from '@/types/s3/directory'
import type { FileObject } from '@/types/s3/fileObject'

export const useS3Accessor = () => {
  const { extractPrefixAndFileName, generateDirectory, getZeroPaddedNumber } = useS3Utils()
  const { downloadObject, uploadData, deleteFile, getFileList } = useMiiaFwS3()

  const getFileFromS3 = async (
    fileName: string,
    prefix: string | null = null,
    bucket: string = MiiaFwConfig.config.s3Access.contentsBucket
  ): Promise<File> => {
    const bucketParam = {
      name: bucket,
      prefix: prefix ? prefix : '',
      delimiter: '/'
    }
    const result = await downloadObject(bucketParam, fileName, 'blob', 600000)
    return new File([result], fileName, { type: result.type })
  }

  /**
   * S3にファイルを保存する
   * @param {string} prefix ファイルディレクトリ名
   * @param {string} fileType ファイルタイプ image/png等
   * @param {string} fileName ファイル名
   * @param {File} fileData Fileオブジェクト
   * @return Promise<any> MiiaFwS3.uploadDataのPromise
   * @note ディレクトリでは、自己判定画像の場合はapplicationIdを、図面画像の場合はtargetIdを使用
   */
  const uploadFileToS3 = async (
    prefix: string,
    fileType: string,
    fileName: string,
    fileData: File
  ): Promise<any> => {
    return await new Promise((resolve, reject) => {
      uploadData(
        {
          name: MiiaFwConfig.config.s3Access.contentsBucket,
          prefix: prefix,
          delimiter: '/'
        },
        fileType,
        fileName,
        fileData
      )
        .then((result: unknown) => {
          resolve(result)
        })
        .catch((error: unknown) => {
          reject(error)
        })
    })
  }

  /**
   * S3 Bucket内のファイルを削除
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns Promise<void>
   */
  const deleteFileFromS3 = async (prefix: string, fileName: string): Promise<void> => {
    const bucketParam = {
      name: MiiaFwConfig.config.s3Access.contentsBucket,
      prefix: prefix,
      delimiter: '/'
    }
    return await deleteFile(bucketParam, fileName)
  }

  /**
   * ファイル一覧の取得
   * @param {Directory} directoryParams 検索対象のS3ディレクトリ
   * @returns Promise<void>
   */
  const getFileListFromS3 = async (directoryParams: Directory): Promise<any> => {
    const prefix = generateDirectory({
      govCode: directoryParams.govCode,
      disasterId: directoryParams.disasterId
    })

    return await new Promise((resolve, reject) => {
      getFileList({
        name: MiiaFwConfig.config.s3Access.contentsBucket,
        prefix: prefix,
        delimiter: '/'
      })
        .then((result: unknown) => {
          resolve(result)
        })
        .catch((error: any) => {
          reject(error)
        })
    })
  }

  /**
   * S3に一括データアップロード
   * @param {FileObject[]} files ファイルデータ
   * @param {Directory} directoryParams アップロード先のS3ディレクトリ
   * @returns Promise<void>
   */
  const uploadFileToS3Batch = async (
    files: FileObject[],
    directoryParams: Directory
  ): Promise<void> => {
    const prefix = generateDirectory({
      govCode: directoryParams.govCode,
      disasterId: directoryParams.disasterId
    })
    const currentTime = Math.floor(Date.now() / 1000) // エポック秒
    const paddedCurrentTime = getZeroPaddedNumber(currentTime, 11) // アップロード日時（11桁0埋）

    const promises = files.map(async (file, index: number) => {
      const fileData = file.file
      const fileType = fileData.type
      const extension = fileType.split('/')[1]
      const fileName = `${paddedCurrentTime}_${getZeroPaddedNumber(index + 1, 4)}.${extension}` // {アップロード日時（11桁0埋）}_{連番（4桁0埋）}.{拡張子}
      return await uploadFileToS3(prefix, fileType, fileName, file.file)
    })

    try {
      await Promise.all(promises)
    } catch (error: any) {
      throw new Error(error)
    }
  }

  /**
   * S3 Bucket内のファイルを一括削除
   * @param objectKeyList S3オブジェクトキー[]
   * @returns Promise<void>
   */
  const deleteFileFromS3Batch = async (objectKeyList: string[]): Promise<void> => {
    const promises = objectKeyList.map(async objectKey => {
      const { prefix, fileName } = extractPrefixAndFileName(objectKey)
      return await deleteFileFromS3(prefix, fileName)
    })
    try {
      await Promise.all(promises)
    } catch (error: any) {
      throw new Error(error)
    }
  }

  return {
    getFileFromS3,
    uploadFileToS3,
    getFileListFromS3,
    deleteFileFromS3,
    uploadFileToS3Batch,
    deleteFileFromS3Batch
  }
}
